import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteEntry } from '@/app/routes.generated';
import { ErrorFrame } from '@/components/layouts';
import { useSession } from '@/mocks';

/**
 * FC-5 路由守衛：依 Session role 判定這個 route 能不能顯示，判定依據是
 * `route.visibleTo`（由 `scripts/build-routes.ts` 從 `legacy-visible.generated.ts` 灌入，
 * 即三角色 raw DOM 143×3 可達性矩陣，逐字鏡像 legacy 的判定）。
 *
 * 【為什麼不是 404】
 * legacy 對「沒權限」的回應是 HTTP 200 的 FORBIDDEN 錯誤殼（`.error-template`），
 * 不是路由層級的「找不到這個網址」。React 沿用同一語意：非 reachable 時渲染
 * `ErrorFrame`，不是把整個 route 從 router 拿掉或導到 404。
 *
 * 【verdict → 畫面的對應，逐字沿用 legacy-visible.generated.ts 的四種判定】
 *   reachable → 正常渲染 children
 *   forbidden → ErrorFrame variant="unauthorized"（Sorry! 很抱歉！FORBIDDEN Access…）
 *   error     → ErrorFrame variant="error"（Ooops! 程式發生錯誤！，legacy 的系統錯誤殼）
 *   redirect  → <Navigate to="/index" replace />（legacy 這格是登入頁落點，SPA 沒有真的
 *               未登入態，導回首頁比導去登入頁更貼近「這裡沒有內容可看」的實際行為）
 *
 * `route.visibleTo === null` 時（這個 pageId 在 legacy 三角色矩陣裡找不到對應，
 * 例如 login／redirect／unresolved 類頁型）一律放行——**不是**「預設可達」的猜測，
 * 是誠實反映「這個維度沒有材料可比對」，contract 那邊會如實記錄同一件事
 * （`legacyUrl` 對不到 `pageId` 時 visibleTo 為 null）。
 */
export interface RoleGuardProps {
  route: RouteEntry;
  children: ReactNode;
}

export function RoleGuard({ route, children }: RoleGuardProps) {
  const { session } = useSession();
  const verdict = route.visibleTo?.[session.role] ?? 'reachable';

  if (verdict === 'reachable') return children;
  if (verdict === 'redirect') return <Navigate to="/index" replace />;
  return (
    <ErrorFrame
      uiId={`${route.pageId}-role-guard`}
      variant={verdict === 'forbidden' ? 'unauthorized' : 'error'}
    />
  );
}
